"use client";

import { useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const stories = [{
  area: "AGRICULTURA",
  title: "Otimização de sistemas de rega e pulverização.",
  summary: "Implementação de bicos de precisão em 500 hectares, reduzindo o desperdício de água em 30% e aumentando a eficácia da aplicação de nutrientes.",
  partner: "AgroCampo Lda",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/agricultura_pulverizacao.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/agricultura_pulverizacao.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/agricultura_pulverizacao.png-wvc-sizes",
  alt: "Sistemas de pulverização industrial aplicados em ambiente agrícola"
}, {
  area: "ALIMENTAR",
  title: "Sistemas de lavagem de tanques de alta pressão.",
  summary: "Automação do processo de higienização em linhas de engarrafamento, garantindo conformidade total com normas de segurança alimentar e reduzindo tempo de paragem.",
  partner: "Indústrias Alimentares S.A.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_tanques.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_tanques.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_tanques.png-wvc-sizes",
  alt: "Equipamento de lavagem de tanques em aço inoxidável"
}, {
  area: "AUTOMÓVEL",
  title: "Aplicação de lubrificantes em componentes.",
  summary: "Integração de sistemas de pulverização de precisão para lubrificação de peças metálicas, assegurando uniformidade e reduzindo o consumo de lubrificante.",
  partner: "AutoComponentes Portugal",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/automovel_lubrificacao.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/automovel_lubrificacao.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/automovel_lubrificacao.png-wvc-sizes",
  alt: "Linha de montagem automóvel com sistemas de pulverização"
}, {
  area: "FARMACÊUTICA",
  title: "Sistemas de desinfeção de ambientes críticos.",
  summary: "Instalação de sistemas de nebulização para desinfeção de salas limpas, garantindo um ambiente estéril com controlo rigoroso de partículas.",
  partner: "FarmaTech Solutions",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/farmaceutica_desinfeccao.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/farmaceutica_desinfeccao.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/farmaceutica_desinfeccao.png-wvc-sizes",
  alt: "Equipamento de desinfeção para ambientes farmacêuticos"
}, {
  area: "QUÍMICA",
  title: "Processamento e mistura de fluidos.",
  summary: "Otimização de bicos de mistura para processos químicos complexos, melhorando a homogeneidade do produto final e a segurança operacional.",
  partner: "Química Industrial Ibérica",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/quimica_processamento.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/quimica_processamento.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/quimica_processamento.webp-wvc-sizes",
  alt: "Processamento químico industrial com sistemas de pulverização"
}, {
  area: "SUSTENTABILIDADE",
  title: "Redução de emissões e consumo de recursos.",
  summary: "Soluções de pulverização que minimizam o impacto ambiental, reduzindo o consumo de químicos e energia em processos industriais de larga escala.",
  partner: "EcoIndustrial Group",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/sustentabilidade_industrial.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/sustentabilidade_industrial.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/sustentabilidade_industrial.png-wvc-sizes",
  alt: "Tecnologia industrial focada em sustentabilidade"
}];
export default function Catalog() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".story-header", {
        y: 24
      });
      gsap.to(".story-header", {
        opacity: 1,
        y: 0,
        duration: 0.64,
        ease: "power3.out",
        stagger: 0.08,
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".story-card", {
        y: 24
      });
      gsap.to(".story-card", {
        opacity: 1,
        y: 0,
        duration: 0.64,
        ease: "power3.out",
        stagger: 0.12,
        scrollTrigger: {
          trigger: ".story-grid",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".story-header", ".story-card"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31375" id="technicalcataloggrid" ref={container} className="bg-background text-foreground py-24 md:py-44">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8">
          <div className="lg:col-span-5 story-header" style={{
          opacity: 0
        }}>
            <p className="text-xs font-medium uppercase mb-6" style={{
            letterSpacing: "0.22em",
            color: "var(--muted-foreground)"
          }}>
              Casos de Sucesso
            </p>
            <h2 className="font-default font-medium leading-[1.06]" style={{
            fontSize: "clamp(2.125rem, 4.6vw, 4rem)",
            letterSpacing: "-0.024em"
          }}>
              Projetos reais.{" "}
              <span style={{
              color: "var(--primary)"
            }}>Resultados</span> concretos.
            </h2>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-between gap-8 story-header" style={{
          opacity: 0
        }}>
            <p className="font-default" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
            color: "var(--muted-foreground)"
          }}>
              Seis exemplos de intervenções em diversos setores, onde a Eurocontrol 
              otimizou processos de pulverização industrial com tecnologia de ponta.
            </p>
            <div className="flex lg:justify-end">
              <Link to="/ApplicationsPage" className="group inline-flex items-center gap-2 text-foreground transition-colors hover:opacity-80" style={{
              fontSize: "0.9375rem"
            }}>
                <span>Ver todas as aplicações</span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>

        <div className="story-grid mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {stories.map((story, i) => <article key={i} data-index={i} className="story-card group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-[320ms] hover:-translate-y-1" style={{
          opacity: 0,
          backgroundColor: "color-mix(in srgb, var(--card) 55%, transparent)",
          backdropFilter: "blur(24px) saturate(140%)",
          WebkitBackdropFilter: "blur(24px) saturate(140%)",
          borderColor: "var(--border)"
        }} onMouseEnter={e => {
          e.currentTarget.style.borderColor = "var(--primary)";
          e.currentTarget.style.boxShadow = "inset 0 0 60px hsl(215 80% 20% / 0.06)";
        }} onMouseLeave={e => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.boxShadow = "none";
        }}>
              <div className="relative overflow-hidden" style={{
            aspectRatio: "16 / 10"
          }}>
                <img src={story.image} data-wvc-srcset={story.srcset} data-wvc-sizes={story.sizes} alt={story.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(180deg, hsl(215 80% 20% / 0.35) 0%, hsl(215 80% 20% / 0.15) 40%, hsl(215 80% 20% / 0.55) 100%)"
            }} />
                <span className="absolute top-6 left-6 text-xs font-medium uppercase" style={{
              letterSpacing: "0.22em",
              color: "var(--primary-foreground)"
            }}>
                  {story.area}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-6 md:p-8">
                <h3 className="font-default font-medium mb-4" style={{
              fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)",
              lineHeight: 1.2,
              letterSpacing: "-0.018em"
            }}>
                  {story.title}
                </h3>
                <p className="font-default flex-1" style={{
              fontSize: "0.9375rem",
              lineHeight: 1.6,
              color: "var(--muted-foreground)"
            }}>
                  {story.summary}
                </p>

                <div className="mt-8 pt-6 flex items-center justify-between gap-4 border-t" style={{
              borderColor: "var(--border)"
            }}>
                  <span className="text-sm" style={{
                color: "var(--foreground)"
              }}>
                    {story.partner}
                  </span>
                  <Button asChild variant="ghost" size="sm" className="h-auto px-2 py-1 gap-1.5 bg-transparent hover:bg-transparent text-foreground hover:text-foreground">
                    <Link to="/ApplicationsPage" className="group/link inline-flex items-center">
                      <span>Ler caso</span>
                      <ArrowUpRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" strokeWidth={1.25} />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}